<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';
  import { Step } from '@client/components/steps';

  import { AdForm, CampaignForm, TargetingForm } from '@client/pages/marketing/campaigns/manage/modals/shared';

  import { CampaignsService } from '@client/services/campaigns';
  import { TargetingService } from '@client/services/targeting';

  import { appStore } from '@client/stores/app';

  import { AD_TYPES, LINK_TYPES } from '@client/enums';

  const { addNotification } = getNotificationsContext();

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  let tagFilters = [];
  let rulename = '';
  let enableSchedule = false;
  let selectedTags = [];
  let selectedCategories = [];
  let startDate = '';
  let endDate = '';
  let selectedFolders = [];
  let invalidRulename = false;

  let linktype = LINK_TYPES.url.id;
  let adtype = AD_TYPES.image.id;
  let url = '';
  let textString = '';
  let textCss = '';
  let fileData = {};
  let files = [];
  let alttext = '';
  let validationError = false;
  let selectedDocuments = '';
  let selectedVideos = '';
  let inline = false;
  let is2x = false;
  let isPlaceholder = false;
  let countInternal = false;
  let disableViews = false;

  let recipients = [];
  let domains = [];
  let listFieldError = false;

  let isDisabled = false;

  let currentInternalStep = 0;
  let maxStepReached = 0;

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const handleInternalNext = (stepIndex) => {
    if (stepIndex < 2) {
      currentInternalStep = stepIndex + 1;

      if (currentInternalStep > maxStepReached) {
        maxStepReached = currentInternalStep;
      }
    }
  };

  const handleInternalBack = (stepIndex) => {
    if (stepIndex > 0) {
      currentInternalStep = stepIndex - 1;
    }
  };

  const executeCreateCampaign = async () => {
    $appStore.loading = true;

    let categories = {};
    selectedCategories.forEach((category) => {
      categories[category.value] = category.checked;
    });

    let processedStartDate = startDate;
    let processedEndDate = endDate;
    if (enableSchedule) {
      processedEndDate = new Date(endDate).toISOString();
      processedStartDate = new Date(startDate).toISOString();
    }

    const campaignData = {
      rulename,
      enableschedule: enableSchedule,
      schedule: enableSchedule
        ? {
            startDate: processedStartDate,
            endDate: processedEndDate
          }
        : null,
      tags: selectedTags.map((tag) => ({ text: tag.value })),
      category: categories,
      folderid: selectedFolders.length ? selectedFolders[0] : null,
      paused: false,
      linktype,
      url: linktype === LINK_TYPES.url.id ? url : null,
      docinfo:
        linktype === LINK_TYPES.doc.id
          ? {
              filename: selectedDocuments?.filename,
              filesize: selectedDocuments?.filesize,
              hash: selectedDocuments?.hash,
              noncompliant: selectedDocuments?.noncompliant,
              rulename: selectedDocuments?.rulename,
              viewonly: selectedDocuments?.viewonly,
              _id: selectedDocuments?._id
            }
          : null,
      docname: linktype === LINK_TYPES.doc.id ? selectedDocuments?.rulename : null,
      videoinfo:
        linktype === LINK_TYPES.video.id
          ? {
              duration: selectedVideos?.duration,
              height: selectedVideos?.height,
              rulename: selectedVideos?.rulename,
              thumbnail: selectedVideos?.thumbnail,
              title: selectedVideos?.title,
              url: selectedVideos?.url,
              videotype: selectedVideos?.videotype,
              vidid: selectedVideos?.vidid,
              width: selectedVideos?.width,
              _id: selectedVideos?._id
            }
          : null,
      videoname: linktype === LINK_TYPES.video.id ? selectedVideos?.rulename : null,
      adtype,
      textstring: adtype === AD_TYPES.text.id ? textString : null,
      textcss: adtype === AD_TYPES.text.id ? textCss : null,
      filename: adtype === AD_TYPES.image.id ? fileData?.filename || null : null,
      filesize: adtype === AD_TYPES.image.id ? fileData?.filesize || null : null,
      hash: adtype === AD_TYPES.image.id ? fileData?.hash || null : null,
      metadata: adtype === AD_TYPES.image.id ? fileData?.metadata || null : null,
      mimetype: adtype === AD_TYPES.image.id ? fileData?.metadata?.mimetype || null : null,
      alttext: adtype === AD_TYPES.image.id ? alttext || null : null,
      inline: adtype === AD_TYPES.image.id ? inline : null,
      is2x: adtype === AD_TYPES.image.id ? is2x : null,
      isplaceholder: adtype === AD_TYPES.image.id ? isPlaceholder : null,
      countinternal: countInternal,
      disableviews: disableViews
    };

    const campaignTargeting = [...recipients, ...domains];
    const activeTargeting = await TargetingService.get();

    campaignTargeting.forEach((name) => {
      const existingAccount = activeTargeting.accounts.find((account) => account.name === name);

      if (existingAccount) {
        const existingAd = existingAccount.ads.find(({ text }) => text === campaignData.rulename);

        if (!existingAd) {
          existingAccount.ads.push({
            text: campaignData.rulename
          });
        }
      } else {
        activeTargeting.accounts.push({
          name,
          ads: [
            {
              text: campaignData.rulename
            }
          ],
          tags: [],
          isNew: true
        });
      }
    });

    const targetingResponse = await TargetingService.update(activeTargeting);

    if (targetingResponse?.status >= 400) {
      $appStore.loading = false;
      notify(
        $format('label.FAILURE.CREATE', {
          values: { type: campaignData.rulename }
        }),
        'error'
      );
      throw new Error(targetingResponse?.message || $format('label.FAILURE.UPDATE_TARGETING'));
    }

    const campaignResponse = await CampaignsService.createCampaign(campaignData);

    $appStore.loading = false;

    if (campaignResponse?.status >= 400) {
      notify($format('label.FAILURE.CREATE', { values: { type: campaignData.rulename } }), 'error');
      throw new Error($format('label.FAILURE.CREATE_CAMPAIGN'));
    }

    notify($format('label.SUCCESS.CREATE', { values: { type: campaignData.rulename } }));

    step.data = {
      id: campaignData.rulename
    };

    return campaignResponse;
  };

  const rollbackCreateCampaign = async () => {
    if (!step.data?.id) {
      return; // Nothing to rollback
    }

    $appStore.loading = true;

    try {
      // Get the campaign that was created
      const campaigns = await CampaignsService.getAll();
      const campaign = campaigns.find((c) => c.rulename === step.data.id);

      if (campaign) {
        const activeTargeting = await TargetingService.get();

        const currentAccountTargeting = activeTargeting.accounts
          .filter(({ ads }) => ads.filter(({ text }) => text === campaign.rulename).length)
          .map(({ name }) => name);

        const updatedAccounts = activeTargeting.accounts.map((account) => {
          if (currentAccountTargeting.includes(account.name)) {
            account.ads = account.ads.filter(({ text }) => text !== campaign.rulename);
          }

          return account;
        });

        const combinedTargeting = {
          accounts: updatedAccounts,
          departments: activeTargeting.departments
        };

        const targetingResponse = await TargetingService.update(combinedTargeting);

        if (targetingResponse?.status >= 400) {
          throw new Error($format('label.FAILURE.UPDATE_TARGETING_ROLLBACK'));
        }

        const campaignDeleteResponse = await CampaignsService.deleteCampaign(campaign);

        if (campaignDeleteResponse?.status >= 400) {
          throw new Error($format('label.FAILURE.DELETE_CAMPAIGN_ROLLBACK'));
        }
      }
    } catch (error) {
      console.error('Failed to rollback campaign creation:', error);
    } finally {
      $appStore.loading = false;
    }
  };

  onMount(() => {
    if (onExecute) {
      onExecute(executeCreateCampaign);
    }
    if (onRollback) {
      onRollback(rollbackCreateCampaign);
    }
  });

  $: {
    if (currentInternalStep === 0) {
      isDisabled = invalidRulename || !rulename.length;
    } else if (currentInternalStep === 1) {
      isDisabled =
        (linktype === LINK_TYPES.url.id && (validationError || !url.length)) ||
        (linktype === LINK_TYPES.doc.id && !Object.keys(selectedDocuments).length) ||
        (linktype === LINK_TYPES.video.id && !Object.keys(selectedVideos).length) ||
        (adtype === AD_TYPES.text.id && !Object.keys(textString).length) ||
        (adtype === AD_TYPES.image.id && !files.length);
    } else if (currentInternalStep === 2) {
      isDisabled = listFieldError;
    }
  }

  $: nextDisabled = currentInternalStep !== 2 || isDisabled;
</script>

<div class="campaign-create-form">
  <Step
    active={currentInternalStep === 0}
    disabled={false}
    title={$format('label.CAMPAIGN')}
    summary={rulename || $format('label.NOT_CONFIGURED')}
    onNext={currentInternalStep === 0 ? () => handleInternalNext(0) : undefined}
    onStep={() => (currentInternalStep = 0)}
    nextDisabled={isDisabled}
  >
    <CampaignForm
      {tagFilters}
      bind:rulename
      bind:enableSchedule
      bind:selectedTags
      bind:selectedCategories
      bind:startDate
      bind:endDate
      bind:selectedFolders
      bind:invalidRulename
    />
  </Step>

  <Step
    active={currentInternalStep === 1}
    disabled={maxStepReached < 1}
    title={$format('label.AD_SETUP')}
    summary={adtype === 'image'
      ? $format('label.IMAGE_AD')
      : adtype === 'text'
        ? $format('label.TEXT_AD')
        : $format('label.VIDEO_AD')}
    onNext={currentInternalStep === 1 ? () => handleInternalNext(1) : undefined}
    onBack={currentInternalStep === 1 ? () => handleInternalBack(1) : undefined}
    onStep={() => {
      if (currentInternalStep >= 1) currentInternalStep = 1;
    }}
    nextDisabled={isDisabled}
  >
    <AdForm
      bind:linktype
      bind:adtype
      bind:url
      bind:textString
      bind:textCss
      bind:fileData
      bind:files
      bind:alttext
      bind:validationError
      bind:selectedDocuments
      bind:selectedVideos
      bind:inline
      bind:is2x
      bind:isPlaceholder
      bind:countInternal
      bind:disableViews
    />
  </Step>

  <Step
    active={currentInternalStep === 2}
    disabled={maxStepReached < 2}
    title={$format('label.TARGETING')}
    summary={recipients.length || domains.length
      ? `${recipients.length} recipients, ${domains.length} domains`
      : $format('label.NOT_CONFIGURED')}
    onBack={currentInternalStep === 2 ? () => handleInternalBack(2) : undefined}
    onStep={() => {
      if (currentInternalStep >= 2) currentInternalStep = 2;
    }}
    nextDisabled={isDisabled}
  >
    <TargetingForm bind:recipients bind:domains bind:listFieldError />
  </Step>
</div>

<style lang="scss">
  .campaign-create-form {
    :global {
      .campaign-details {
        border-radius: var(--border-radius-base);
        padding: var(--padding-small) var(--padding-base);

        .campaign-item {
          display: flex;
          padding: var(--padding-large);

          + .campaign-item {
            border-top: 1px solid var(--border-color);
          }
        }

        .campaign-info {
          align-items: center;
          flex: 1;
          margin-right: var(--margin-base);

          b {
            color: var(--color-gray-900);
          }
        }

        .campaign-actions {
          align-items: center;
          display: flex;
          gap: 16px;
          justify-content: flex-end;
          width: 275px;
        }
      }

      .invalid-feedback {
        color: var(--color-red-500);
        font-size: var(--font-size-medium);
        margin-top: 0;
      }
    }
  }

  @media only screen and (max-width: 800px) {
    .campaign-create-form {
      :global {
        .campaign-details {
          .campaign-item {
            display: flex;
            flex-direction: column;
            gap: 16px;

            .file {
              display: flex;
              flex-direction: column;
              gap: 16px;
            }

            .campaign-actions {
              justify-content: flex-start;
              width: 100%;

              &.ad {
                width: 100%;

                .text-ad {
                  width: 100%;
                }
              }

              .image-ad {
                .file-item {
                  padding: var(--padding-base) 0;
                }
              }

              .action-button {
                justify-content: start;
              }
            }
          }
        }
      }
    }
  }
</style>
